import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, FileType2, Upload, Download, Trash2, Loader2, Paperclip, EyeOff, Lock, Users } from "lucide-react";

const KB = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const KIND_ICON = { pdf: FileType2, docx: FileText, txt: FileText };

export const RRHHDocuments = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [dl, setDl] = useState(null);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: "", category: "General", published: true, file: null, roles: [] });

  const listQ = useQuery({
    queryKey: ["rrhh-documents"],
    queryFn: async () => (await api.get("/rrhh/documents")).data,
  });
  const rolesQ = useQuery({ queryKey: ["roles"], queryFn: async () => (await api.get("/roles")).data });
  const roleList = rolesQ.data || [];
  const roleName = (k) => roleList.find((r) => r.key === k)?.name || k;
  const items = listQ.data?.items || [];
  const canManage = !!listQ.data?.can_manage;

  const toggleRole = (k) => setForm((s) => ({ ...s, roles: s.roles.includes(k) ? s.roles.filter((x) => x !== k) : [...s.roles, k] }));

  const uploadMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("title", form.title.trim());
      fd.append("category", form.category.trim() || "General");
      fd.append("published", String(form.published));
      fd.append("roles", form.roles.join(","));
      return (await api.post("/rrhh/documents", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rrhh-documents"] });
      toast.success("Documento subido. El Asistente IA ya puede consultarlo.");
      setOpen(false);
      setForm({ title: "", category: "General", published: true, file: null, roles: [] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/rrhh/documents/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rrhh-documents"] }); setConfirmDel(null); toast.success("Documento eliminado"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const pubMut = useMutation({
    mutationFn: async ({ id, published }) => {
      const fd = new FormData();
      fd.append("published", String(published));
      return (await api.patch(`/rrhh/documents/${id}`, fd)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rrhh-documents"] }),
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const download = async (d) => {
    setDl(d.id);
    try {
      const res = await api.get(`/rrhh/documents/${d.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setDl(null);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setForm((s) => ({ ...s, file: f, title: s.title || f.name.replace(/\.[^.]+$/, "") }));
  };

  if (!canManage && items.length === 0) return null;

  return (
    <Card data-testid="rrhh-documents">
      <CardContent className="p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Paperclip className="h-5 w-5 text-primary" />Documentos adjuntos</h2>
            <p className="text-xs text-muted-foreground">Convenio, manual del empleado, políticas… El Asistente IA los usa para responder tus dudas.</p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)} data-testid="rrhh-doc-upload-btn"><Upload className="mr-1.5 h-4 w-4" />Subir documento</Button>
          )}
        </div>

        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="rrhh-doc-empty">Aún no hay documentos subidos.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {items.map((d) => {
              const Icon = KIND_ICON[d.kind] || FileText;
              return (
                <div key={d.id} data-testid={`rrhh-doc-${d.id}`} className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4.5 w-4.5" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate text-sm font-semibold">{d.title}</p>
                      {!d.published && <EyeOff className="h-3.5 w-3.5 shrink-0 text-amber-500" title="Borrador (no visible para empleados)" />}
                    </div>
                    <p className="text-[11px] text-muted-foreground">{d.category} · {d.kind?.toUpperCase()} · {KB(d.size)}</p>
                    <div className="mt-0.5">
                      {d.roles?.length ? (
                        <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`rrhh-doc-access-${d.id}`}><Lock className="h-3 w-3" />{d.roles.map(roleName).join(", ")}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground" data-testid={`rrhh-doc-access-${d.id}`}><Users className="h-3 w-3" />Todos los perfiles</span>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => download(d)} disabled={dl === d.id} data-testid={`rrhh-doc-download-${d.id}`} title="Abrir / descargar">
                    {dl === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </Button>
                  {canManage && (
                    <>
                      <Switch checked={!!d.published} onCheckedChange={(v) => pubMut.mutate({ id: d.id, published: v })} data-testid={`rrhh-doc-pub-${d.id}`} title="Publicado" />
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => setConfirmDel(d)} data-testid={`rrhh-doc-del-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { if (!uploadMut.isPending) setOpen(v); }}>
        <DialogContent data-testid="rrhh-doc-dialog">
          <DialogHeader><DialogTitle>Subir documento al portal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Archivo (PDF, DOCX o TXT · máx. 15 MB)</Label>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" onChange={onPickFile} className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground" data-testid="rrhh-doc-file" />
            </div>
            <div>
              <Label htmlFor="doc-title">Título</Label>
              <Input id="doc-title" value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} placeholder="p. ej. Convenio colectivo 2026" data-testid="rrhh-doc-title" />
            </div>
            <div>
              <Label htmlFor="doc-cat">Categoría</Label>
              <Input id="doc-cat" value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} placeholder="General" data-testid="rrhh-doc-category" />
            </div>
            <div>
              <Label>Acceso (perfiles)</Label>
              <p className="mb-1.5 text-[11px] text-muted-foreground">Sin seleccionar = visible para todos. Elige perfiles para restringirlo (p. ej. solo Traveler Support verá un manual de soporte).</p>
              <div className="flex flex-wrap gap-1.5">
                {roleList.map((r) => {
                  const on = form.roles.includes(r.key);
                  return (
                    <button key={r.key} type="button" onClick={() => toggleRole(r.key)} data-testid={`rrhh-doc-role-${r.key}`}
                      className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}>
                      {r.name}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1.5 flex items-center gap-1 text-[11px] font-medium">
                {form.roles.length ? <><Lock className="h-3 w-3 text-amber-600" />Solo: {form.roles.map(roleName).join(", ")}</> : <><Users className="h-3 w-3 text-muted-foreground" />Visible para todos los perfiles</>}
              </p>
            </div>
            <div className="flex items-center justify-between rounded-lg border px-3 py-2">
              <div><p className="text-sm font-medium">Publicado</p><p className="text-[11px] text-muted-foreground">Visible para el equipo y para el Asistente IA</p></div>
              <Switch checked={form.published} onCheckedChange={(v) => setForm((s) => ({ ...s, published: v }))} data-testid="rrhh-doc-published" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={uploadMut.isPending}>Cancelar</Button>
            <Button onClick={() => uploadMut.mutate()} disabled={uploadMut.isPending || !form.file || !form.title.trim()} data-testid="rrhh-doc-submit">
              {uploadMut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Procesando…</> : <><Upload className="mr-1.5 h-4 w-4" />Subir</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Eliminar documento</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">¿Seguro que quieres eliminar «{confirmDel?.title}»? El Asistente IA dejará de consultarlo.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => delMut.mutate(confirmDel.id)} disabled={delMut.isPending} data-testid="rrhh-doc-confirm-del">
              {delMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RRHHDocuments;
